import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
} from "@material-ui/core";
import { Add as AddIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";
import { useStyles } from "./planManagerStyles";
import { PlanFormImproved } from "./PlanFormImproved";
import { PlansManagerGridImproved } from "./PlansManagerGridImproved";

const INITIAL_PLAN_RECORD = {
  id: undefined,
  name: "",
  users: 0,
  connections: 0,
  queues: 0,
  value: 0,
  useCampaigns: true,
  useSchedules: true,
  useInternalChat: true,
  useExternalApi: true,
  useKanban: true,
  useOpenAi: true,
  useIntegrations: true,
  useInternal: true
};

export default function PlansManagerImproved() {
  const classes = useStyles();
  const { list, save, update, remove } = usePlans();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [record, setRecord] = useState(() => ({ ...INITIAL_PLAN_RECORD }));

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const planList = await list();
      setRecords(planList);
    } catch (e) {
      toast.error(i18n.t("plans.messages.loadError"));
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadPlans();
      handleCancel();
      toast.success(i18n.t("plans.messages.saveSuccess"));
    } catch (e) {
      toast.error(i18n.t("plans.messages.saveError"));
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCancel();
      toast.success(i18n.t("plans.messages.deleteSuccess"));
    } catch (e) {
      toast.error(i18n.t("plans.messages.deleteError"));
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setPlanModalOpen(false);
    setRecord({ ...INITIAL_PLAN_RECORD });
  };

  const handleOpenCreateModal = () => {
    setRecord({ ...INITIAL_PLAN_RECORD });
    setPlanModalOpen(true);
  };

  const handleSelect = (data) => {
    let useCampaigns = data.useCampaigns === false ? false : true;
    let useSchedules = data.useSchedules === false ? false : true;
    let useInternalChat = data.useInternalChat === false ? false : true;
    let useExternalApi = data.useExternalApi === false ? false : true;
    let useKanban = data.useKanban === false ? false : true;
    let useOpenAi = data.useOpenAi === false ? false : true;
    let useIntegrations = data.useIntegrations === false ? false : true;
    let useInternal = data.useInternal === true ? true : false;

    setRecord({
      id: data.id,
      name: data.name || '',
      users: data.users || 0,
      connections: data.connections || 0,
      queues: data.queues || 0,
      value: data.value?.toLocaleString('pt-br', { minimumFractionDigits: 0 }) || 0,
      useCampaigns,
      useSchedules,
      useInternalChat,
      useExternalApi,
      useKanban,
      useOpenAi,
      useIntegrations,
      useInternal
    });
    setPlanModalOpen(true);
  };

  return (
    <div className={classes.root}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" style={{ fontWeight: "bold" }}>
          {i18n.t("plans.title")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateModal}
          className={classes.primaryButton}
        >
          {i18n.t("plans.registerButton")}
        </Button>
      </Box>

      <Dialog
        open={planModalOpen}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          style: {
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column"
          }
        }}
      >
        <DialogContent
          style={{
            padding: 0,
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column"
          }}
        >
          <PlanFormImproved
            variant="dialog"
            initialValue={record}
            onDelete={handleOpenDeleteDialog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </DialogContent>
      </Dialog>

      <PlansManagerGridImproved
        records={records}
        onSelect={handleSelect}
      />

      <ConfirmationModal
        title={i18n.t("plans.messages.deleteTitle")}
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        {i18n.t("plans.messages.deleteMessage")}
      </ConfirmationModal>
    </div>
  );
}
